import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckSquare,
  ChevronDown,
  Download,
  Filter,
  History,
  ListPlus,
  MoreHorizontal,
  Pencil,
  Plus,
  Tag as TagIcon,
  Trash2,
  UserCheck,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { Column } from '@/components/shared/data-table';
import { RecordSection } from '@/components/shared/record-section';
import { useUserOptions } from '@/hooks/use-user-options';
import { usePermission } from '@/hooks/use-permission';
import { useSegments } from '@/features/vault/api';
import { downloadCsv } from '@/features/sales/api';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost, apiClient } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────────────────────

export type ObjectType = 'contact' | 'company' | 'lead' | 'deal';
export type Condition = { field: string; op: string; value?: unknown };
export type FilterSet = { match: 'ALL' | 'ANY'; conditions: Condition[] };
export type FieldDef = { key: string; label: string; kind: string; options: string[] | null };
export type PropertyType =
  | 'TEXT'
  | 'TEXTAREA'
  | 'NUMBER'
  | 'CURRENCY'
  | 'DATE'
  | 'DATETIME'
  | 'SELECT'
  | 'MULTISELECT'
  | 'CHECKBOX'
  | 'URL'
  | 'EMAIL'
  | 'PHONE'
  | 'USER'
  | 'FORMULA';
export type Property = {
  id: string;
  key: string;
  label: string;
  type: PropertyType;
  options: { choices?: string[]; formula?: string; group?: string; description?: string } | null;
  required: boolean;
  displayOrder: number;
  visibleToRoles?: string[];
  editableByRoles?: string[];
  /** Whether this person's role may change it (field-level permissions). */
  canEdit?: boolean;
};
export type SavedView = {
  id: string;
  name: string;
  filters: FilterSet & { q?: string; ownerId?: string };
  columns: string[];
  sort: string | null;
  isShared: boolean;
  userId: string;
  user: { name: string } | null;
};
export type Tag = { id: string; name: string; colour: string };

const ENDPOINT: Record<ObjectType, string> = { contact: '/contacts', company: '/companies', lead: '/leads', deal: '/deals' };
const QUERY_KEY: Record<ObjectType, string> = { contact: 'contacts', company: 'companies', lead: 'leads', deal: 'deals' };

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);

// ── Data ─────────────────────────────────────────────────────────────────────

export function useFields(type: ObjectType) {
  return useQuery({
    queryKey: ['records', type, 'fields'],
    queryFn: () => apiGet<{ builtIn: FieldDef[]; custom: FieldDef[] }>(`/records/${type}/fields`),
    staleTime: 60_000,
  });
}

export function useProperties(type: ObjectType) {
  return useQuery({
    queryKey: ['records', type, 'properties'],
    queryFn: () => apiGet<Property[]>(`/records/${type}/properties`),
    staleTime: 60_000,
  });
}

export function useSaveProperty(type: ObjectType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Property> & { id?: string }) =>
      id ? apiPatch<Property>(`/records/properties/${id}`, body) : apiPost<Property>(`/records/${type}/properties`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['records', type] }),
  });
}

export function useDeleteProperty(type: ObjectType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/records/properties/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['records', type] }),
  });
}

export function useViews(type: ObjectType) {
  return useQuery({ queryKey: ['records', type, 'views'], queryFn: () => apiGet<SavedView[]>(`/records/${type}/views`) });
}

export function useTags() {
  return useQuery({ queryKey: ['tags'], queryFn: () => apiGet<Tag[]>('/tags'), staleTime: 60_000 });
}

/** Saves one record's custom property values (the server merges them). */
export function useSaveCustomValues(type: ObjectType, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (customFields: Record<string, unknown>) => apiPatch(`${ENDPOINT[type]}/${id}`, { customFields }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY[type]] });
      void queryClient.invalidateQueries({ queryKey: ['records', type, id] });
    },
  });
}

// ── List state ───────────────────────────────────────────────────────────────

type BuiltInView = { key: string; label: string; ownerId?: string; filters?: Condition[] };

/**
 * Everything a list screen remembers: the open view (built-in or saved),
 * extra filters, search, owner, sort and paging — turned into the query the
 * API understands.
 */
export function useRecordList(type: ObjectType, builtIn: BuiltInView[], meId?: string) {
  const views = useViews(type);
  const storageKey = `dovo.view.${type}`;
  const [active, setActive] = useState<string>(() => {
    try {
      return localStorage.getItem(storageKey) ?? builtIn[0].key;
    } catch {
      return builtIn[0].key;
    }
  });
  const [extra, setExtra] = useState<FilterSet>({ match: 'ALL', conditions: [] });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    try {
      return Number(localStorage.getItem('dovo.pageSize')) || 25;
    } catch {
      return 25;
    }
  });

  const saved = views.data?.find((view) => view.id === active);
  const base = builtIn.find((view) => view.key === active) ?? (saved ? undefined : builtIn[0]);

  // A saved view brings its own search and sort when it is opened.
  useEffect(() => {
    if (saved) {
      setSearch(saved.filters.q ?? '');
      setSort(saved.sort ?? '');
    }
    setExtra({ match: 'ALL', conditions: [] });
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, saved?.id]);

  const choose = (key: string) => {
    setActive(key);
    try {
      localStorage.setItem(storageKey, key);
    } catch {
      // Not remembered.
    }
  };

  const viewConditions = saved?.filters.conditions ?? base?.filters ?? [];
  const conditions = [...viewConditions, ...extra.conditions];
  const match = extra.conditions.length && viewConditions.length ? 'ALL' : (saved?.filters.match ?? extra.match);
  const ownerRaw = saved?.filters.ownerId ?? base?.ownerId;
  const ownerId = ownerRaw === 'me' ? meId : ownerRaw;

  const params = {
    page,
    limit,
    q: search || undefined,
    sort: sort || undefined,
    ownerId,
    filters: conditions.length ? JSON.stringify({ match, conditions }) : undefined,
  };

  return {
    views: views.data ?? [],
    builtIn,
    active: saved || builtIn.some((view) => view.key === active) ? active : builtIn[0].key,
    choose,
    saved,
    extra,
    setExtra: (next: FilterSet) => {
      setExtra(next);
      setPage(1);
    },
    search,
    setSearch: (value: string) => {
      setSearch(value);
      setPage(1);
    },
    sort,
    setSort,
    page,
    setPage,
    limit,
    setLimit: (value: number) => {
      setLimit(value);
      setPage(1);
      try {
        localStorage.setItem('dovo.pageSize', String(value));
      } catch {
        // Not remembered.
      }
    },
    params,
    /** What "Save as view" stores: the open view's filters plus the extra ones. */
    snapshot: { match, conditions, q: search || undefined, ownerId: ownerRaw } as SavedView['filters'],
    refetchViews: views.refetch,
  };
}

export type RecordList = ReturnType<typeof useRecordList>;

// ── View tabs ────────────────────────────────────────────────────────────────

export function ViewTabs({ type, list }: { type: ObjectType; list: RecordList }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState<'new' | SavedView | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/records/views/${id}`),
    onSuccess: () => {
      list.choose(list.builtIn[0].key);
      void queryClient.invalidateQueries({ queryKey: ['records', type, 'views'] });
    },
    onError: (error) => failed(error, 'Could not delete the view'),
  });

  const tab = (key: string, label: string, view?: SavedView) => {
    const active = list.active === key;
    return (
      <div
        key={key}
        className={cn(
          'group relative flex shrink-0 items-center rounded-t-md transition-colors',
          active ? 'bg-muted/70 text-foreground' : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
        )}
      >
        <button type="button" onClick={() => list.choose(key)} className="px-3 py-2 text-sm font-semibold">
          {t(label)}
          {view?.isShared && <span className="ms-1.5 text-[10px] font-bold uppercase text-muted-foreground">{t('shared')}</span>}
        </button>
        {view && active && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="me-1 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label={t('View options')}>
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onSelect={() => setSaving(view)}>
                <Pencil className="h-4 w-4" /> {t('Rename or share')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setSaving({ ...view, filters: list.snapshot, sort: list.sort || null })}>
                <ListPlus className="h-4 w-4" /> {t('Save current filters to this view')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={() => remove.mutate(view.id)}>
                <Trash2 className="h-4 w-4" /> {t('Delete view')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-foreground" />}
      </div>
    );
  };

  return (
    <div className="scrollbar-thin -mb-px mt-3 flex items-end gap-1 overflow-x-auto border-b">
      {list.builtIn.map((view) => tab(view.key, view.label))}
      {list.views.map((view) => tab(view.id, view.name, view))}
      <button
        type="button"
        onClick={() => setSaving('new')}
        className="mb-1 ms-1 flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-sm font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
        title={t('Save the current filters as a view')}
      >
        <Plus className="h-4 w-4" /> {t('Add view')}
      </button>
      {saving && <SaveViewDialog type={type} list={list} view={saving === 'new' ? null : saving} onClose={() => setSaving(null)} />}
    </div>
  );
}

function SaveViewDialog({ type, list, view, onClose }: { type: ObjectType; list: RecordList; view: SavedView | null; onClose: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState(view?.name ?? '');
  const [shared, setShared] = useState(view?.isShared ?? false);
  const save = useMutation({
    mutationFn: () =>
      view
        ? apiPatch<SavedView>(`/records/views/${view.id}`, { name, isShared: shared, filters: view.filters, sort: view.sort })
        : apiPost<SavedView>(`/records/${type}/views`, { name, isShared: shared, filters: list.snapshot, sort: list.sort || null }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['records', type, 'views'] });
      list.choose(saved.id);
      toast.success(t('View saved'));
      onClose();
    },
    onError: (error) => failed(error, 'Could not save the view'),
  });
  const count = (view?.filters ?? list.snapshot).conditions.length;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{view ? t('Edit view') : t('Save as a new view')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('View name')}</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={t('e.g. Dubai VIPs')} autoFocus />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('{count} filter conditions will be saved with this view.', { count })}
          </p>
          <label className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
            <span>
              <span className="font-semibold">{t('Share with everyone')}</span>
              <span className="block text-xs text-muted-foreground">{t('Your whole workspace sees this view as a tab.')}</span>
            </span>
            <Switch checked={shared} onCheckedChange={setShared} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!name.trim() || save.isPending}>
            {t('Save view')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Advanced filters ─────────────────────────────────────────────────────────

const OPS: Record<string, { value: string; label: string; noValue?: boolean }[]> = {
  text: [
    { value: 'contains', label: 'contains' },
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'not_contains', label: "doesn't contain" },
    { value: 'starts_with', label: 'starts with' },
    { value: 'is_not_empty', label: 'is known', noValue: true },
    { value: 'is_empty', label: 'is unknown', noValue: true },
  ],
  number: [
    { value: 'eq', label: 'is equal to' },
    { value: 'gt', label: 'is greater than' },
    { value: 'gte', label: 'is at least' },
    { value: 'lt', label: 'is less than' },
    { value: 'lte', label: 'is at most' },
    { value: 'is_not_empty', label: 'is known', noValue: true },
    { value: 'is_empty', label: 'is unknown', noValue: true },
  ],
  date: [
    { value: 'in_last_days', label: 'in the last (days)' },
    { value: 'in_next_days', label: 'in the next (days)' },
    { value: 'more_than_days_ago', label: 'more than (days) ago' },
    { value: 'on', label: 'is on' },
    { value: 'before', label: 'is before' },
    { value: 'after', label: 'is after' },
    { value: 'is_not_empty', label: 'is known', noValue: true },
    { value: 'is_empty', label: 'is unknown', noValue: true },
  ],
  bool: [
    { value: 'is_true', label: 'is yes', noValue: true },
    { value: 'is_false', label: 'is no', noValue: true },
  ],
  choice: [
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
    { value: 'is_not_empty', label: 'is known', noValue: true },
    { value: 'is_empty', label: 'is unknown', noValue: true },
  ],
};

function opsFor(kind: string) {
  if (['number', 'money', 'NUMBER', 'CURRENCY', 'FORMULA'].includes(kind)) return OPS.number;
  if (['date', 'DATE', 'DATETIME'].includes(kind)) return kind === 'date' ? OPS.date : OPS.date.filter((op) => ['on', 'is_empty', 'is_not_empty'].includes(op.value));
  if (['bool', 'CHECKBOX'].includes(kind)) return OPS.bool;
  if (['enum', 'user', 'ref', 'SELECT', 'MULTISELECT', 'tag'].includes(kind)) return OPS.choice;
  return OPS.text;
}

export function FilterButton({ type, list }: { type: ObjectType; list: RecordList }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const count = list.extra.conditions.length;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[13px] font-semibold transition-colors',
          count ? 'border-primary/40 bg-primary/[0.07]' : 'text-foreground/80 hover:bg-accent',
        )}
      >
        <Filter className="h-3.5 w-3.5" />
        {t('Advanced filters')}
        {count > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] text-white">{count}</span>}
      </button>
      {open && <FilterDialog type={type} value={list.extra} onApply={list.setExtra} onClose={() => setOpen(false)} />}
    </>
  );
}

function FilterDialog({ type, value, onApply, onClose }: { type: ObjectType; value: FilterSet; onApply: (next: FilterSet) => void; onClose: () => void }) {
  const t = useT();
  const { data: fields } = useFields(type);
  const { data: users = [] } = useUserOptions();
  const { data: tags = [] } = useTags();
  const [draft, setDraft] = useState<FilterSet>(value);

  const catalogue = useMemo<FieldDef[]>(
    () => [...(fields?.builtIn ?? []), { key: 'tag', label: 'Tags', kind: 'tag', options: null }, ...(fields?.custom ?? [])],
    [fields],
  );
  const byKey = new Map(catalogue.map((field) => [field.key, field]));

  const update = (index: number, patch: Partial<Condition>) =>
    setDraft((current) => ({ ...current, conditions: current.conditions.map((item, i) => (i === index ? { ...item, ...patch } : item)) }));

  const choicesFor = (field: FieldDef): { value: string; label: string }[] | null => {
    if (field.kind === 'user') return users.map((user) => ({ value: user.id, label: user.name }));
    if (field.kind === 'tag') return tags.map((tag) => ({ value: tag.id, label: tag.name }));
    if (field.options) return field.options.map((option) => ({ value: option, label: option.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) }));
    return null;
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('Advanced filters')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {draft.conditions.length > 1 && (
            <div className="flex items-center gap-2 text-sm">
              {t('Show records that match')}
              <Select
                className="h-8 w-auto"
                value={draft.match}
                onChange={(event) => setDraft({ ...draft, match: event.target.value as 'ALL' | 'ANY' })}
              >
                <option value="ALL">{t('all of these')}</option>
                <option value="ANY">{t('any of these')}</option>
              </Select>
            </div>
          )}
          {draft.conditions.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t('No filters yet. Add one to narrow the list by any property.')}
            </p>
          )}
          {draft.conditions.map((condition, index) => {
            const field = byKey.get(condition.field);
            const ops = opsFor(field?.kind ?? 'text');
            const op = ops.find((item) => item.value === condition.op) ?? ops[0];
            const choices = field ? choicesFor(field) : null;
            const selected = Array.isArray(condition.value) ? (condition.value as string[]) : [];
            return (
              <div key={index} className="rounded-lg border bg-muted/30 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  {index > 0 && <span className="text-xs font-bold uppercase text-muted-foreground">{draft.match === 'ANY' ? t('or') : t('and')}</span>}
                  <Select
                    className="h-8 w-auto min-w-[11rem]"
                    value={condition.field}
                    onChange={(event) => {
                      const next = byKey.get(event.target.value);
                      update(index, { field: event.target.value, op: opsFor(next?.kind ?? 'text')[0].value, value: undefined });
                    }}
                  >
                    <optgroup label={t('Properties')}>
                      {(fields?.builtIn ?? []).map((item) => (
                        <option key={item.key} value={item.key}>
                          {t(item.label)}
                        </option>
                      ))}
                      <option value="tag">{t('Tags')}</option>
                    </optgroup>
                    {(fields?.custom ?? []).length > 0 && (
                      <optgroup label={t('Custom properties')}>
                        {fields!.custom.map((item) => (
                          <option key={item.key} value={item.key}>
                            {item.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </Select>
                  <Select className="h-8 w-auto" value={op.value} onChange={(event) => update(index, { op: event.target.value })}>
                    {ops.map((item) => (
                      <option key={item.value} value={item.value}>
                        {t(item.label)}
                      </option>
                    ))}
                  </Select>
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, conditions: draft.conditions.filter((_, i) => i !== index) })}
                    className="ms-auto rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"
                    aria-label={t('Remove')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {!op.noValue && (
                  <div className="mt-2">
                    {choices && ['in', 'not_in'].includes(op.value) ? (
                      <div className="flex max-h-36 flex-wrap gap-1.5 overflow-y-auto">
                        {choices.map((choice) => {
                          const on = selected.includes(choice.value);
                          return (
                            <button
                              key={choice.value}
                              type="button"
                              onClick={() => update(index, { value: on ? selected.filter((v) => v !== choice.value) : [...selected, choice.value] })}
                              className={cn('rounded-full border px-2.5 py-1 text-xs font-semibold', on ? 'border-primary bg-primary text-white' : 'bg-card hover:bg-accent')}
                            >
                              {choice.label}
                            </button>
                          );
                        })}
                        {choices.length === 0 && <span className="text-xs text-muted-foreground">{t('Nothing to choose from yet.')}</span>}
                      </div>
                    ) : (
                      <Input
                        className="h-8"
                        type={['on', 'before', 'after'].includes(op.value) ? 'date' : ['in_last_days', 'in_next_days', 'more_than_days_ago'].includes(op.value) || opsFor(field?.kind ?? '') === OPS.number ? 'number' : 'text'}
                        value={String(condition.value ?? '')}
                        onChange={(event) => update(index, { value: event.target.value })}
                        placeholder={t('Value')}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setDraft({ ...draft, conditions: [...draft.conditions, { field: fields?.builtIn[0]?.key ?? 'createdAt', op: opsFor(fields?.builtIn[0]?.kind ?? 'user')[0].value }] })
            }
          >
            <Plus /> {t('Add filter')}
          </Button>
        </div>
        <DialogFooter>
          {value.conditions.length > 0 && (
            <Button
              variant="ghost"
              className="me-auto"
              onClick={() => {
                onApply({ match: 'ALL', conditions: [] });
                onClose();
              }}
            >
              {t('Clear all')}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => {
              onApply({ ...draft, conditions: draft.conditions.filter((item) => item.field && item.op) });
              onClose();
            }}
          >
            {t('Apply filters')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The owner pill: anyone, me, unassigned or a named colleague. */
export function OwnerPill({ value, onChange, label = 'Owner' }: { value: string; onChange: (value: string) => void; label?: string }) {
  const t = useT();
  const { data: users = [] } = useUserOptions();
  const chosen = value === 'none' ? t('Unassigned') : users.find((user) => user.id === value)?.name;
  return (
    <label
      className={cn(
        'relative inline-flex h-8 cursor-pointer items-center gap-1 rounded-full border px-3 text-[13px] font-semibold transition-colors',
        value ? 'border-primary/40 bg-primary/[0.07]' : 'text-foreground/80 hover:bg-accent',
      )}
    >
      {t(label)}
      {chosen && <span className="max-w-[9rem] truncate font-normal text-muted-foreground">: {chosen}</span>}
      <ChevronDown className="h-3 w-3 opacity-60" />
      <select value={value} onChange={(event) => onChange(event.target.value)} className="absolute inset-0 cursor-pointer opacity-0" aria-label={t(label)}>
        <option value="">{t('Any')}</option>
        <option value="none">{t('Unassigned')}</option>
        {users.map((user) => (
          <option key={user.id} value={user.id}>
            {user.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// ── Export ───────────────────────────────────────────────────────────────────

export function ExportViewButton({ type, list }: { type: ObjectType; list: RecordList }) {
  const t = useT();
  const { can } = usePermission();
  if (!can(`crm.${type}.export`)) return null;
  return (
    <button
      type="button"
      onClick={() => {
        const query = new URLSearchParams();
        if (list.params.filters) query.set('filters', list.params.filters);
        if (list.params.q) query.set('q', list.params.q);
        if (list.params.ownerId) query.set('ownerId', list.params.ownerId);
        void downloadCsv(`/records/${type}/export?${query.toString()}`, `${type}s.csv`).catch(() => toast.error(t('Export failed')));
      }}
      className="flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
      title={t('Export this view, every page')}
    >
      <Download className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{t('Export')}</span>
    </button>
  );
}

// ── Bulk actions ─────────────────────────────────────────────────────────────

type BulkKind = 'assign' | 'update' | 'tag' | 'segment' | 'task';

export function BulkMenu({ type, ids, onDone }: { type: ObjectType; ids: string[]; onDone: () => void }) {
  const t = useT();
  const { can } = usePermission();
  const [open, setOpen] = useState<BulkKind | null>(null);
  const pill = 'inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-semibold transition-colors hover:bg-accent';
  return (
    <>
      {can(`crm.${type}.assign`) && (
        <button type="button" className={pill} onClick={() => setOpen('assign')}>
          <UserCheck className="h-3.5 w-3.5" /> {t('Assign')}
        </button>
      )}
      {can(`crm.${type}.update`) && (
        <>
          <button type="button" className={pill} onClick={() => setOpen('update')}>
            <Pencil className="h-3.5 w-3.5" /> {t('Edit')}
          </button>
          <button type="button" className={pill} onClick={() => setOpen('tag')}>
            <TagIcon className="h-3.5 w-3.5" /> {t('Tags')}
          </button>
        </>
      )}
      {type === 'contact' && can('marketing.segment.update') && (
        <button type="button" className={pill} onClick={() => setOpen('segment')}>
          <ListPlus className="h-3.5 w-3.5" /> {t('Add to segment')}
        </button>
      )}
      {can('crm.task.create') && (
        <button type="button" className={pill} onClick={() => setOpen('task')}>
          <CheckSquare className="h-3.5 w-3.5" /> {t('Create tasks')}
        </button>
      )}
      {open && (
        <BulkDialog
          type={type}
          ids={ids}
          kind={open}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onDone();
          }}
        />
      )}
    </>
  );
}

const BULK_FIELDS: Record<ObjectType, string[]> = {
  contact: ['designation', 'department', 'isDecisionMaker', 'emailOptIn', 'whatsappOptIn', 'language'],
  company: ['industry', 'type', 'lifecycleStage', 'website'],
  lead: ['status', 'source', 'campaign', 'industry', 'city', 'country'],
  deal: ['dealType', 'source', 'campaign', 'expectedCloseDate', 'probability'],
};

function BulkDialog({ type, ids, kind, onClose, onDone }: { type: ObjectType; ids: string[]; kind: BulkKind; onClose: () => void; onDone: () => void }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: users = [] } = useUserOptions();
  const { data: fields } = useFields(type);
  const { data: tags = [] } = useTags();
  const { data: segments = [] } = useSegments();
  const [ownerId, setOwnerId] = useState('');
  const [field, setField] = useState('');
  const [value, setValue] = useState<string>('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [newTag, setNewTag] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [task, setTask] = useState({ title: '', dueAt: '', type: 'TODO', assigneeId: '' });

  const editable = [
    ...(fields?.builtIn ?? []).filter((item) => BULK_FIELDS[type].includes(item.key)),
    ...(fields?.custom ?? []).filter((item) => item.kind !== 'FORMULA'),
  ];
  const fieldDef = editable.find((item) => item.key === field);

  const run = useMutation({
    mutationFn: async () => {
      let body: Record<string, unknown>;
      if (kind === 'assign') body = { action: 'assign', ids, ownerId: ownerId || null };
      else if (kind === 'update') {
        let v: unknown = value;
        if (fieldDef && ['bool', 'CHECKBOX'].includes(fieldDef.kind)) v = value === 'true';
        body = { action: 'update', ids, field, value: v };
      } else if (kind === 'tag') {
        const extra = newTag.trim() ? [(await apiPost<Tag>('/tags', { name: newTag.trim() })).id] : [];
        body = { action: 'tag', ids, tagIds: [...tagIds, ...extra], mode };
      } else if (kind === 'segment') body = { action: 'segment', ids, segmentId };
      else body = { action: 'task', ids, title: task.title, dueAt: task.dueAt || undefined, type: task.type, assigneeId: task.assigneeId || undefined };
      return apiPost<{ updated: number }>(`/records/${type}/bulk`, body);
    },
    onSuccess: (result) => {
      toast.success(t('{count} updated', { count: result.updated }));
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEY[type]] });
      void queryClient.invalidateQueries({ queryKey: ['tags'] });
      onDone();
    },
    onError: (error) => failed(error, 'That did not work'),
  });

  const titles: Record<BulkKind, string> = {
    assign: 'Assign owner',
    update: 'Edit a property',
    tag: 'Add or remove tags',
    segment: 'Add to a static segment',
    task: 'Create a task for each record',
  };
  const ready =
    kind === 'assign' ||
    (kind === 'update' && field) ||
    (kind === 'tag' && (tagIds.length || newTag.trim())) ||
    (kind === 'segment' && segmentId) ||
    (kind === 'task' && task.title.trim());

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(titles[kind])} <span className="text-muted-foreground">({ids.length})</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {kind === 'assign' && (
            <div className="space-y-1.5">
              <Label>{t('Owner')}</Label>
              <Select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
                <option value="">{t('No owner')}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {kind === 'update' && (
            <>
              <div className="space-y-1.5">
                <Label>{t('Property')}</Label>
                <Select value={field} onChange={(event) => setField(event.target.value)}>
                  <option value="">{t('Choose a property…')}</option>
                  {editable.map((item) => (
                    <option key={item.key} value={item.key}>
                      {t(item.label)}
                    </option>
                  ))}
                </Select>
              </div>
              {fieldDef && (
                <div className="space-y-1.5">
                  <Label>{t('New value')}</Label>
                  {['bool', 'CHECKBOX'].includes(fieldDef.kind) ? (
                    <Select value={value} onChange={(event) => setValue(event.target.value)}>
                      <option value="true">{t('Yes')}</option>
                      <option value="false">{t('No')}</option>
                    </Select>
                  ) : fieldDef.options ? (
                    <Select value={value} onChange={(event) => setValue(event.target.value)}>
                      <option value="">{t('(clear)')}</option>
                      {fieldDef.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type={['date', 'DATE'].includes(fieldDef.kind) ? 'date' : ['number', 'NUMBER', 'CURRENCY'].includes(fieldDef.kind) ? 'number' : 'text'}
                      value={value}
                      onChange={(event) => setValue(event.target.value)}
                    />
                  )}
                </div>
              )}
            </>
          )}
          {kind === 'tag' && (
            <>
              <div className="flex gap-2">
                {(['add', 'remove'] as const).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setMode(item)}
                    className={cn('flex-1 rounded-md border py-1.5 text-sm font-semibold', mode === item ? 'border-foreground bg-foreground text-background' : 'hover:bg-accent')}
                  >
                    {item === 'add' ? t('Add tags') : t('Remove tags')}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => {
                  const on = tagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => setTagIds(on ? tagIds.filter((id) => id !== tag.id) : [...tagIds, tag.id])}
                      className={cn('rounded-full border px-2.5 py-1 text-xs font-semibold', on ? 'text-white' : 'bg-card hover:bg-accent')}
                      style={on ? { backgroundColor: tag.colour, borderColor: tag.colour } : undefined}
                    >
                      {tag.name}
                    </button>
                  );
                })}
              </div>
              {mode === 'add' && <Input value={newTag} onChange={(event) => setNewTag(event.target.value)} placeholder={t('…or type a new tag')} />}
            </>
          )}
          {kind === 'segment' && (
            <Select value={segmentId} onChange={(event) => setSegmentId(event.target.value)}>
              <option value="">{t('Choose a static segment…')}</option>
              {segments
                .filter((segment) => !segment.isDynamic)
                .map((segment) => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name}
                  </option>
                ))}
            </Select>
          )}
          {kind === 'task' && (
            <>
              <div className="space-y-1.5">
                <Label>{t('Task')}</Label>
                <Input value={task.title} onChange={(event) => setTask({ ...task, title: event.target.value })} placeholder={t('e.g. Call to follow up')} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t('Type')}</Label>
                  <Select value={task.type} onChange={(event) => setTask({ ...task, type: event.target.value })}>
                    {['TODO', 'CALL', 'EMAIL', 'MEETING', 'FOLLOW_UP'].map((item) => (
                      <option key={item} value={item}>
                        {t(item.replace('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()))}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t('Due')}</Label>
                  <Input type="datetime-local" value={task.dueAt} onChange={(event) => setTask({ ...task, dueAt: event.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t('Assign to')}</Label>
                <Select value={task.assigneeId} onChange={(event) => setTask({ ...task, assigneeId: event.target.value })}>
                  <option value="">{t('Me')}</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => run.mutate()} disabled={!ready || run.isPending}>
            {t('Apply to {count}', { count: ids.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Custom properties on lists and records ───────────────────────────────────

export function formatCustomValue(value: unknown, type: PropertyType): ReactNode {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) return value.join(', ');
  if (type === 'CHECKBOX') return value ? 'Yes' : 'No';
  if (type === 'CURRENCY') return `AED ${Number(value).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (type === 'NUMBER' || type === 'FORMULA') return Number(value).toLocaleString('en-AE');
  if (type === 'DATE' || type === 'DATETIME') return formatDate(String(value));
  if (type === 'URL')
    return (
      <a href={String(value)} target="_blank" rel="noreferrer" className="text-link hover:underline" onClick={(event) => event.stopPropagation()}>
        {String(value).replace(/^https?:\/\//, '')}
      </a>
    );
  return String(value);
}

/** List columns for every custom property, hidden until switched on. */
export function customColumns<T extends { customFields?: Record<string, unknown> | null }>(properties: Property[]): Column<T>[] {
  return properties.map((property) => ({
    key: `cf.${property.key}`,
    header: property.label,
    hidden: true,
    className: 'text-sm',
    cell: (row: T) => formatCustomValue(row.customFields?.[property.key], property.type) ?? <span className="text-muted-foreground">--</span>,
  }));
}

/**
 * The custom properties block in a record's About panel: every property with
 * its value, edited in place one at a time.
 */
export function CustomPropertiesSection({
  type,
  id,
  values,
  canEdit,
}: {
  type: ObjectType;
  id: string;
  values: Record<string, unknown> | null | undefined;
  canEdit: boolean;
}) {
  const t = useT();
  const { data: properties = [] } = useProperties(type);
  const save = useSaveCustomValues(type, id);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<unknown>(null);
  const { data: users = [] } = useUserOptions();
  if (properties.length === 0) return null;

  const commit = (key: string, value: unknown) =>
    save.mutate(
      { [key]: value },
      {
        onSuccess: () => setEditing(null),
        onError: (error) => failed(error, 'Could not save'),
      },
    );

  const groups = new Map<string, Property[]>();
  for (const property of properties) {
    const group = property.options?.group || 'Custom properties';
    groups.set(group, [...(groups.get(group) ?? []), property]);
  }

  return (
    <>
      {[...groups.entries()].map(([group, items]) => (
        <RecordSection key={group} title={group} count={items.length}>
          <div className="divide-y">
            {items.map((property) => {
              const value = values?.[property.key];
              const isEditing = editing === property.key;
              const shown = property.type === 'USER' ? users.find((user) => user.id === value)?.name : formatCustomValue(value, property.type);
              return (
                <div key={property.key} className="py-2">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    {property.label}
                    {property.required && <span className="text-destructive">*</span>}
                    {property.type === 'FORMULA' && <span className="rounded bg-muted px-1 text-[10px] font-bold">ƒx</span>}
                  </div>
                  {isEditing ? (
                    <div className="mt-1 flex items-start gap-1.5">
                      <div className="min-w-0 flex-1">
                        <ValueInput property={property} value={draft} onChange={setDraft} users={users} />
                      </div>
                      <Button size="sm" className="h-8" onClick={() => commit(property.key, draft)} disabled={save.isPending}>
                        {t('Save')}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditing(null)}>
                        <X />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={!canEdit || property.canEdit === false || property.type === 'FORMULA'}
                      onClick={() => {
                        setDraft(value ?? (property.type === 'MULTISELECT' ? [] : property.type === 'CHECKBOX' ? false : ''));
                        setEditing(property.key);
                      }}
                      className="group mt-0.5 flex w-full items-center gap-2 rounded text-start text-sm font-medium enabled:hover:bg-muted/60 disabled:cursor-default"
                    >
                      <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{shown ?? <span className="font-normal text-muted-foreground">--</span>}</span>
                      {canEdit && property.canEdit !== false && property.type !== 'FORMULA' && <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </RecordSection>
      ))}
    </>
  );
}

function ValueInput({
  property,
  value,
  onChange,
  users,
}: {
  property: Property;
  value: unknown;
  onChange: (value: unknown) => void;
  users: { id: string; name: string }[];
}) {
  const t = useT();
  const choices = property.options?.choices ?? [];
  switch (property.type) {
    case 'TEXTAREA':
      return <Textarea rows={3} value={String(value ?? '')} onChange={(event) => onChange(event.target.value)} autoFocus />;
    case 'CHECKBOX':
      return (
        <label className="flex h-8 items-center gap-2 text-sm">
          <Checkbox checked={Boolean(value)} onCheckedChange={(checked) => onChange(checked === true)} /> {t('Yes')}
        </label>
      );
    case 'SELECT':
      return (
        <Select className="h-8" value={String(value ?? '')} onChange={(event) => onChange(event.target.value)}>
          <option value="">--</option>
          {choices.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </Select>
      );
    case 'MULTISELECT': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-wrap gap-1">
          {choices.map((choice) => {
            const on = selected.includes(choice);
            return (
              <button
                key={choice}
                type="button"
                onClick={() => onChange(on ? selected.filter((item) => item !== choice) : [...selected, choice])}
                className={cn('rounded-full border px-2 py-0.5 text-xs font-semibold', on ? 'border-primary bg-primary text-white' : 'hover:bg-accent')}
              >
                {choice}
              </button>
            );
          })}
        </div>
      );
    }
    case 'USER':
      return (
        <Select className="h-8" value={String(value ?? '')} onChange={(event) => onChange(event.target.value)}>
          <option value="">--</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </Select>
      );
    default: {
      const type =
        property.type === 'NUMBER' || property.type === 'CURRENCY'
          ? 'number'
          : property.type === 'DATE'
            ? 'date'
            : property.type === 'DATETIME'
              ? 'datetime-local'
              : property.type === 'EMAIL'
                ? 'email'
                : property.type === 'PHONE'
                  ? 'tel'
                  : 'text';
      const shown = property.type === 'DATE' && value ? String(value).slice(0, 10) : property.type === 'DATETIME' && value ? String(value).slice(0, 16) : String(value ?? '');
      return <Input className="h-8" type={type} value={shown} onChange={(event) => onChange(event.target.value)} autoFocus />;
    }
  }
}

// ── Tags on a record ─────────────────────────────────────────────────────────

export function RecordTags({ type, id, canEdit }: { type: ObjectType; id: string; canEdit: boolean }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: all = [] } = useTags();
  const { data: mine = [] } = useQuery({ queryKey: ['records', type, id, 'tags'], queryFn: () => apiGet<Tag[]>(`/records/${type}/${id}/tags`) });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const set = useMutation({
    mutationFn: (tagIds: string[]) => apiClient.put(`/records/${type}/${id}/tags`, { tagIds }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['records', type, id, 'tags'] }),
    onError: (error) => failed(error, 'Could not change the tags'),
  });
  const create = useMutation({
    mutationFn: (tagName: string) => apiPost<Tag>('/tags', { name: tagName }),
    onSuccess: (tag) => {
      void queryClient.invalidateQueries({ queryKey: ['tags'] });
      set.mutate([...mine.map((item) => item.id), tag.id]);
      setName('');
      setAdding(false);
    },
    onError: (error) => failed(error, 'Could not create the tag'),
  });
  const mineIds = new Set(mine.map((tag) => tag.id));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {mine.map((tag) => (
        <span key={tag.id} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ backgroundColor: tag.colour }}>
          {tag.name}
          {canEdit && (
            <button type="button" onClick={() => set.mutate(mine.filter((item) => item.id !== tag.id).map((item) => item.id))} aria-label={t('Remove')}>
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {canEdit &&
        (adding ? (
          <span className="flex items-center gap-1">
            <input
              list={`tags-${id}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || !name.trim()) return;
                const existing = all.find((tag) => tag.name.toLowerCase() === name.trim().toLowerCase());
                if (existing) {
                  if (!mineIds.has(existing.id)) set.mutate([...mineIds, existing.id]);
                  setName('');
                  setAdding(false);
                } else create.mutate(name.trim());
              }}
              autoFocus
              placeholder={t('Tag name, Enter')}
              className="h-6 w-32 rounded border bg-card px-2 text-xs outline-none focus:border-primary"
            />
            <datalist id={`tags-${id}`}>
              {all.filter((tag) => !mineIds.has(tag.id)).map((tag) => (
                <option key={tag.id} value={tag.name} />
              ))}
            </datalist>
            <button type="button" onClick={() => setAdding(false)} className="text-muted-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed px-2 py-0.5 text-[11px] font-semibold text-muted-foreground hover:border-primary hover:text-primary"
          >
            <Plus className="h-3 w-3" /> {t('Tag')}
          </button>
        ))}
    </div>
  );
}

// ── Property history ─────────────────────────────────────────────────────────

type Change = { field: string; from: unknown; to: unknown; at: string; by: string | null; action: string };

export function HistoryButton({ type, id }: { type: ObjectType; id: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-link inline-flex items-center gap-1 text-xs font-semibold hover:underline">
        <History className="h-3.5 w-3.5" /> {t('View property history')}
      </button>
      {open && <HistoryDialog type={type} id={id} onClose={() => setOpen(false)} />}
    </>
  );
}

function HistoryDialog({ type, id, onClose }: { type: ObjectType; id: string; onClose: () => void }) {
  const t = useT();
  const { data: changes = [], isLoading } = useQuery({ queryKey: ['records', type, id, 'history'], queryFn: () => apiGet<Change[]>(`/records/${type}/${id}/history`) });
  const { data: properties = [] } = useProperties(type);
  const { data: fields } = useFields(type);
  const labelOf = (field: string) =>
    field.startsWith('cf.')
      ? (properties.find((property) => `cf.${property.key}` === field)?.label ?? field.slice(3))
      : (fields?.builtIn.find((item) => item.key === field)?.label ?? field.replace(/([A-Z])/g, ' $1').toLowerCase());
  const show = (value: unknown) => (value === null || value === undefined || value === '' ? '--' : typeof value === 'object' ? JSON.stringify(value) : String(value));
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('Property history')}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('Loading…')}</p>
        ) : changes.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('No changes recorded yet.')}</p>
        ) : (
          <ol className="max-h-[60vh] space-y-2 overflow-y-auto">
            {changes.map((change, index) => (
              <li key={index} className="rounded-md border p-2.5 text-sm">
                {change.action === 'update' ? (
                  <p>
                    <span className="font-semibold">{t(labelOf(change.field))}</span>: <span className="text-muted-foreground line-through">{show(change.from)}</span> →{' '}
                    <span className="font-medium">{show(change.to)}</span>
                  </p>
                ) : (
                  <p className="font-semibold capitalize">{change.action.replace(/_/g, ' ')}</p>
                )}
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {change.by ?? t('System')} · {timeAgo(change.at)}
                </p>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Associations with labels ─────────────────────────────────────────────────

type Association = { id: string; type: ObjectType | 'ticket'; recordId: string; name: string; label: string | null };
const ASSOC_TYPES: { type: ObjectType; label: string; path: string; listPath: string }[] = [
  { type: 'contact', label: 'Contact', path: 'contacts', listPath: '/contacts' },
  { type: 'company', label: 'Company', path: 'companies', listPath: '/companies' },
  { type: 'deal', label: 'Deal', path: 'deals', listPath: '/deals' },
  { type: 'lead', label: 'Lead', path: 'leads', listPath: '/leads' },
];

/** "Associations" on a record's right column: any linked record, with a label. */
export function AssociationsSection({ type, id }: { type: ObjectType; id: string }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const { data: rows = [] } = useQuery({ queryKey: ['associations', type, id], queryFn: () => apiGet<Association[]>(`/associations/${type}/${id}`) });
  const { data: labels = [] } = useQuery({ queryKey: ['associations', 'labels'], queryFn: () => apiGet<string[]>('/associations/labels'), staleTime: 300_000 });
  const [adding, setAdding] = useState(false);
  const [target, setTarget] = useState<ObjectType>('contact');
  const [search, setSearch] = useState('');
  const [label, setLabel] = useState('');
  const spec = ASSOC_TYPES.find((item) => item.type === target)!;
  const { data: results } = useQuery({
    queryKey: ['associations', 'search', target, search],
    queryFn: () => apiGet<{ id: string; name?: string; title?: string; firstName?: string; lastName?: string | null }[]>(spec.listPath, { params: { q: search, limit: 8 } }),
    enabled: adding && search.trim().length >= 2,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['associations', type, id] });
  const link = useMutation({
    mutationFn: (toId: string) => apiPost('/associations', { fromType: type, fromId: id, toType: target, toId, label: label || null }),
    onSuccess: () => {
      refresh();
      setAdding(false);
      setSearch('');
      setLabel('');
    },
    onError: (error) => failed(error, 'Could not link the records'),
  });
  const unlink = useMutation({ mutationFn: (associationId: string) => apiDelete(`/associations/${associationId}`), onSuccess: refresh });
  const canEdit = can(`crm.${type}.update`);
  const nameOf = (row: { name?: string; title?: string; firstName?: string; lastName?: string | null }) => row.name ?? row.title ?? [row.firstName, row.lastName].filter(Boolean).join(' ');
  return (
    <RecordSection
      title="Associations"
      count={rows.length}
      action={
        canEdit ? (
          <button type="button" onClick={() => setAdding(!adding)} className="text-link text-xs font-semibold hover:underline">
            + {t('Add')}
          </button>
        ) : undefined
      }
    >
      <div className="space-y-2 pt-2">
        {adding && (
          <div className="space-y-2 rounded-md border bg-muted/40 p-2.5">
            <div className="flex gap-1.5">
              <Select className="h-8 w-auto" value={target} onChange={(event) => setTarget(event.target.value as ObjectType)}>
                {ASSOC_TYPES.map((item) => (
                  <option key={item.type} value={item.type}>
                    {t(item.label)}
                  </option>
                ))}
              </Select>
              <Input className="h-8" list="association-labels" value={label} onChange={(event) => setLabel(event.target.value)} placeholder={t('Label (optional)')} />
              <datalist id="association-labels">
                {labels.map((item) => (
                  <option key={item} value={item} />
                ))}
              </datalist>
            </div>
            <Input className="h-8" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('Search by name…')} autoFocus />
            {(results ?? [])
              .filter((row) => !(target === type && row.id === id))
              .map((row) => (
                <button key={row.id} type="button" onClick={() => link.mutate(row.id)} className="block w-full truncate rounded px-2 py-1.5 text-start text-sm hover:bg-accent">
                  {nameOf(row)}
                </button>
              ))}
          </div>
        )}
        {rows.length === 0 && !adding && <p className="py-1 text-sm text-muted-foreground">{t('No other linked records.')}</p>}
        {rows.map((row) => {
          const path = ASSOC_TYPES.find((item) => item.type === row.type)?.path;
          return (
            <div key={row.id} className="group flex items-center gap-2 rounded-md border p-2.5 text-sm">
              <span className="min-w-0 flex-1">
                <a href={path ? `/crm/${path}/${row.recordId}` : `/service/tickets/${row.recordId}`} className="text-link block truncate font-semibold hover:underline">
                  {row.name}
                </a>
                <span className="text-xs text-muted-foreground">
                  {t(row.type.replace(/^\w/, (c) => c.toUpperCase()))}
                  {row.label ? ` · ${row.label}` : ''}
                </span>
              </span>
              {canEdit && (
                <button type="button" onClick={() => unlink.mutate(row.id)} className="text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100" aria-label={t('Remove')}>
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </RecordSection>
  );
}
